'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth, SignUp, SignIn } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import Ably from 'ably'
import { createAblyClient } from '@/lib/ably'
import api from '@/service/api'
import { recordClipViewWhenSeen } from '@/lib/clip-views'
import { clipLengthsWithin, type ClipDurationLimits } from '@/lib/clip-lengths'
import { apiEvents } from '@/service/request'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Progress } from '@/components/ui/progress'
import {
  ArrowRight,
  Check,
  Loader2,
  AlertCircle,
  X,
  Scissors,
  ChevronLeft,
  ChevronRight,
  Video,
  Sparkles,
} from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { getIconByName } from '@/lib/icon-map'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { PaywallModal } from '@/components/PaywallModal'
import { CAPTION_TEMPLATES } from '@/types/caption'
import { ClipToolAnalytics } from '@/lib/analytics'
import { useCustomer } from '@/lib/hooks/use-customer'
import {
  FREE_PLAN_CREDITS,
  clampSection,
  sectionCapSeconds,
} from '@/lib/section-limits'
import { dfTrackClipExportClicked } from '@/lib/datafast'
import { ClipCard } from '@/components/clips/ClipCard'
import type { ClipData } from '@/components/clips/ClipCard'

// ============================================================================
// Types
// ============================================================================

interface ViralClipsPageClientProps {
  tool: any
  relatedTools: any[]
  categoryTools: any[]
}

interface TranscriptSnippet {
  text: string
  start: number
  duration: number
}

interface TranscriptData {
  snippets: TranscriptSnippet[]
}

interface TranscriptResponse {
  youtube_video_id: string
  youtube_url: string
  title?: string
  channel_name?: string
  thumbnail_url?: string
  duration?: number
  // Optional: only populated by /public/transcript. The landing page's
  // pre-clip-detection flow uses /public/video-metadata which omits this.
  transcript?: TranscriptData
  language?: string
  is_auto_generated?: boolean
  available_languages?: string[]
}

// Live detection events carry the basics; the list API fills in the rest (thumbnail, scenes, caption).
interface ClipCardData extends Omit<ClipData, 'preview_url'> {
  preview_url?: string | null
}

type Phase = 'input' | 'configure' | 'processing' | 'results'

// ============================================================================
// Constants (adapted from ConfigureClient.tsx)
// ============================================================================

const FORMATS = [
  { value: '9:16' as const, label: '9:16', desc: 'Shorts / Reels' },
  { value: '4:5' as const, label: '4:5', desc: 'Instagram Feed' },
  { value: '16:9' as const, label: '16:9', desc: 'YouTube / Twitter' },
]


const STEP_LABELS: Record<string, string> = {
  'Starting...': 'Starting...',
  'Downloading video': 'Downloading video...',
  'Downloading video...': 'Downloading video...',
  'Extracting audio': 'Extracting audio...',
  'Extracting audio...': 'Extracting audio...',
  'Transcribing audio': 'Transcribing with Whisper...',
  'Transcribing...': 'Transcribing with Whisper...',
  'Analyzing content genre': 'Analyzing with AI...',
  'Analyzing transcript...': 'Analyzing with AI...',
  'Identifying viral moments': 'Detecting viral moments...',
  'Detecting viral moments...': 'Detecting viral moments...',
  'Evaluating clip quality': 'Evaluating clip quality...',
  'Creating clip records': 'Creating clip records...',
  'Generating clips': 'Generating clips...',
  'Complete': 'Complete!',
  '': 'Initializing...',
}

// ============================================================================
// Utilities (adapted from ConfigureClient.tsx)
// ============================================================================

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function secondsToHMS(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = Math.floor(totalSeconds % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function formatSliderTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

function sanitizeStep(step: string): string {
  return STEP_LABELS[step] || step.replace(/oxylab|modal|hatchet|internal/gi, '').trim() || 'Processing...'
}

// ============================================================================
// Clip Data Conversion
// ============================================================================

function toClipData(clip: ClipCardData, contentId?: string, videoThumbnail?: string): ClipData & { contentId: string } {
  const duration = clip.duration || (clip.end_time && clip.start_time ? clip.end_time - clip.start_time : 0)
  return {
    ...clip,
    clip_index: clip.clip_index ?? 0,
    start_time: clip.start_time ?? 0,
    end_time: clip.end_time ?? 0,
    duration,
    status: clip.status || 'detected',
    preview_url: clip.preview_url ?? undefined,
    thumbnail_url: clip.thumbnail_url || (clip.preview_url ? undefined : (videoThumbnail || undefined)),
    contentId: contentId || '',
  }
}

// ============================================================================
// Main Component
// ============================================================================

export default function ViralClipsPageClient({ tool, relatedTools, categoryTools }: ViralClipsPageClientProps) {
  const { getToken, isSignedIn, isLoaded } = useAuth()
  const { customer } = useCustomer()
  const searchParams = useSearchParams()
  const router = useRouter()

  // Phase state
  const [phase, setPhase] = useState<Phase>('input')

  // URL & Transcript
  const [url, setUrl] = useState('')
  const [transcript, setTranscript] = useState<TranscriptResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Configuration state
  const [selectedFormat, setSelectedFormat] = useState<'9:16' | '4:5' | '16:9'>('9:16')
  const [clipLength, setClipLength] = useState('auto')
  const [clipDurationLimits, setClipDurationLimits] = useState<ClipDurationLimits | null>(null)
  const [autoHook, setAutoHook] = useState(true)
  const [selectedCaptionIndex, setSelectedCaptionIndex] = useState(0)
  const [sectionStartSec, setSectionStartSec] = useState(0)
  const [sectionEndSec, setSectionEndSec] = useState(0)

  // Processing state
  const [progressPercent, setProgressPercent] = useState(0)
  const [currentStep, setCurrentStep] = useState('')
  const [detectedClips, setDetectedClips] = useState<ClipCardData[]>([])
  const [contentId, setContentId] = useState<string | null>(null)
  const [videoId, setVideoId] = useState<string | null>(null)
  const [processingError, setProcessingError] = useState<string | null>(null)

  // Auth Modal — starts false to avoid hydration mismatch.
  // useEffect checks for #/sso-callback hash after mount.
  const [showAuthModal, setShowAuthModal] = useState(false)

  // Open auth modal after hydration if returning from OAuth with #/sso-callback hash
  useEffect(() => {
    if (window.location.hash.includes('/sso-callback')) {
      setShowAuthModal(true)
    }
  }, [])
  const [authMode, setAuthMode] = useState<'sign-up' | 'sign-in'>('sign-up')

  // Paywall
  const [isPaywallOpen, setIsPaywallOpen] = useState(false)
  const [creditsInfo, setCreditsInfo] = useState<{ required: number; available: number } | null>(null)

  // Refs
  const workspaceRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const resultsScrollRef = useRef<HTMLDivElement>(null)

  // Derived
  const ToolIcon = getIconByName(tool.icon_name)
  const rawDuration = transcript?.duration || 0
  const videoDuration = rawDuration
  // Credits are the only cap on how much of a video one run may cover.
  const creditsBalance = customer ? (customer.features?.credits?.balance ?? 0) : null
  const sectionCap = sectionCapSeconds(creditsBalance)
  const isRangeNarrowed = sectionStartSec > 0 || sectionEndSec < videoDuration

  useEffect(() => {
    api.tools
      .getClipDurationLimits()
      .then((result: any) => setClipDurationLimits(result.data || result))
      .catch(() => {})
  }, [])

  const clipLengths = clipLengthsWithin(clipDurationLimits)
  const clipLengthConfig = clipLengths.find(c => c.value === clipLength) || clipLengths[0]

  // ============================================================================
  // Paywall event listeners
  // ============================================================================

  useEffect(() => {
    const handlePaywallTrigger = () => {
      setIsPaywallOpen(true)
      setProcessingError('Free limit reached. Sign in or upgrade for unlimited generations.')
    }

    const handleInsufficientCredits = (event: any) => {
      const detail = event.detail || {}
      setCreditsInfo({ required: detail.required || 0, available: detail.available || 0 })
      setIsPaywallOpen(true)
      setProcessingError(`Insufficient credits. Required: ${detail.required || 0}, Available: ${detail.available || 0}`)
    }

    apiEvents.addEventListener('paywall-triggered', handlePaywallTrigger)
    apiEvents.addEventListener('insufficient-credits', handleInsufficientCredits)

    return () => {
      apiEvents.removeEventListener('paywall-triggered', handlePaywallTrigger)
      apiEvents.removeEventListener('insufficient-credits', handleInsufficientCredits)
    }
  }, [])

  // ============================================================================
  // Auth flow (reuse pattern from ToolPageClient)
  // ============================================================================

  const wasSignedIn = useRef(isSignedIn)
  useEffect(() => {
    if (isSignedIn && !wasSignedIn.current) {
      wasSignedIn.current = true
      setShowAuthModal(false)

      // User just signed in via modal — transcript + config ready, start generation
      if (transcript && phase === 'configure') {
        startClipGeneration()
        return
      }

      // OAuth redirect recovery
      const saved = sessionStorage.getItem('transcriptr_pending_tool_url')
      if (saved) {
        try {
          const { youtubeUrl } = JSON.parse(saved)
          if (youtubeUrl) {
            window.location.href = `${window.location.origin}/tools/${tool.slug}?url=${encodeURIComponent(youtubeUrl)}`
            return
          }
        } catch { /* ignore */ }
      }
    }
    wasSignedIn.current = isSignedIn
  }, [isSignedIn])

  // Track page view
  useEffect(() => {
    ClipToolAnalytics.pageViewed(tool.slug, tool.category)
  }, [tool.slug, tool.category])

  // Handle page load with ?url= param (OAuth redirect recovery)
  const hasAutoSubmitted = useRef(false)
  useEffect(() => {
    if (!isSignedIn || hasAutoSubmitted.current) return

    const urlParam = searchParams.get('url')
    if (urlParam) {
      hasAutoSubmitted.current = true
      sessionStorage.removeItem('transcriptr_pending_tool_url')
      setUrl(urlParam)
      setTimeout(() => handleSubmitWithUrl(urlParam), 0)
      return
    }

    const saved = sessionStorage.getItem('transcriptr_pending_tool_url')
    if (saved) {
      sessionStorage.removeItem('transcriptr_pending_tool_url')
      try {
        const { youtubeUrl } = JSON.parse(saved)
        if (youtubeUrl) {
          hasAutoSubmitted.current = true
          setUrl(youtubeUrl)
          setTimeout(() => handleSubmitWithUrl(youtubeUrl), 0)
        }
      } catch { /* ignore */ }
    }
  }, [isSignedIn])

  // ============================================================================
  // Phase 1: Input handlers
  // ============================================================================

  const handleSubmitWithUrl = async (submitUrl: string) => {
    if (!submitUrl.trim()) return
    setLoading(true)
    setError(null)

    try {
      // Metadata-only fetch (~150ms via YouTube Data API). Transcript is
      // deferred to clip generation — Whisper handles transcription if
      // captions are missing, so we save the 8s Webshare proxy hop here.
      const metadataResponse = await api.transcript.getVideoMetadata(submitUrl)
      const meta = metadataResponse.data || metadataResponse
      const mapped: TranscriptResponse = {
        youtube_video_id: meta.video_id,
        youtube_url: submitUrl,
        title: meta.title || undefined,
        channel_name: meta.channel || undefined,
        thumbnail_url: meta.thumbnail_url || undefined,
        duration: meta.duration_seconds,
      }
      setTranscript(mapped)
      setSectionEndSec(Math.min(mapped.duration || 0, sectionCap))
      setPhase('configure')
      ClipToolAnalytics.urlPasted(tool.slug, mapped.duration)

      setTimeout(() => {
        workspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err: any) {
      setError(err.response?.data?.detail?.[0]?.msg || err.message || 'Failed to fetch transcript')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = () => handleSubmitWithUrl(url)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSubmit()
  }

  // ============================================================================
  // Phase 2: Configuration handlers
  // ============================================================================

  const handleSliderChange = useCallback((values: number[]) => {
    const [start, end] = clampSection([values[0], values[1]], [sectionStartSec, sectionEndSec], sectionCap)
    setSectionStartSec(start)
    setSectionEndSec(end)
  }, [sectionStartSec, sectionEndSec, sectionCap])

  function scrollCarousel(direction: 'left' | 'right') {
    if (!carouselRef.current) return
    carouselRef.current.scrollBy({
      left: direction === 'left' ? -200 : 200,
      behavior: 'smooth',
    })
  }

  const handleGetClips = () => {
    if (!isLoaded) return
    if (!isSignedIn) {
      sessionStorage.setItem('transcriptr_pending_tool_url', JSON.stringify({ youtubeUrl: url }))
      setShowAuthModal(true)
      return
    }
    startClipGeneration()
  }

  // ============================================================================
  // Phase 3: SSE clip generation with Ably supplementary channel
  // ============================================================================

  const ablyClientRef = useRef<Ably.Realtime | null>(null)
  const ablyChannelRef = useRef<Ably.RealtimeChannel | null>(null)

  // Cleanup Ably on unmount
  useEffect(() => {
    return () => {
      if (ablyChannelRef.current) {
        ablyChannelRef.current.unsubscribe()
        try { ablyChannelRef.current.detach() } catch { /* ignore */ }
      }
      if (ablyClientRef.current) {
        ablyClientRef.current.close()
      }
    }
  }, [])

  function handleAblyEvent(name: string, data: any, activeContentId: string) {
    if (name === 'step_progress') {
      setProgressPercent(data.progress ?? 0)
      setCurrentStep(sanitizeStep(data.step || ''))
    } else if (name === 'clip_detected' || name === 'clip_create') {
      const clip = data.clip || data
      if (clip?.id) {
        setDetectedClips(prev => {
          if (prev.some(c => c.id === clip.id)) return prev
          return [...prev, {
            id: clip.id,
            title: clip.title,
            clip_index: clip.clip_index,
            start_time: clip.start_time,
            end_time: clip.end_time,
            duration: clip.duration,
            virality_score: clip.virality_score,
            virality_reason: clip.virality_reason,
            status: clip.status || 'detected',
            preview_url: clip.preview_url,
          }]
        })
      }
    } else if (name === 'content_completed') {
      recordClipViewWhenSeen(activeContentId, getToken)
      setProgressPercent(100)
      setCurrentStep('Complete!')
      setDetectedClips(prev => {
        const avgScore = prev.length > 0 ? prev.reduce((sum, c) => sum + c.virality_score, 0) / prev.length : 0
        ClipToolAnalytics.detectionCompleted(tool.slug, prev.length, avgScore, transcript?.duration)
        return prev
      })
      // Scene and speaker info is written while clips process, after their detection events; read the finished list.
      void getToken()
        .then((token) => api.clips.listClips(activeContentId, token ?? undefined))
        .then((response) => {
          const clips = response.data as ClipCardData[]
          if (Array.isArray(clips) && clips.length > 0) setDetectedClips(clips)
        })
        .catch((error) => console.warn('Could not refresh the finished clips', error))
      setTimeout(() => setPhase('results'), 500)
    } else if (name === 'content_failed') {
      setProcessingError(data.error || 'An error occurred. Please try again.')
    }
  }

  function subscribeToContent(activeContentId: string) {
    const client = ablyClientRef.current
    if (!client) return

    const channel = client.channels.get(`content:${activeContentId}`)
    ablyChannelRef.current = channel

    channel.subscribe((message: Ably.Message) => {
      if (message.data) {
        handleAblyEvent(message.name ?? '', message.data, activeContentId)
      }
    })
  }

  const startClipGeneration = async () => {
    if (!transcript) return
    ClipToolAnalytics.detectionStarted(tool.slug, transcript?.duration, isSignedIn ?? false)

    setPhase('processing')
    setProgressPercent(0)
    setCurrentStep('Starting...')
    setDetectedClips([])
    setProcessingError(null)
    setContentId(null)
    setVideoId(null)

    try {
      const token = await getToken()

      const config: Record<string, string> = {
        output_format: selectedFormat,
        enable_ai_hooks: String(autoHook),
        max_clip_duration: String(clipLengthConfig.max),
        min_clip_duration: String(clipLengthConfig.min),
        caption_style: JSON.stringify(CAPTION_TEMPLATES[selectedCaptionIndex].style),
      }
      if (sectionStartSec > 0) config.start_time = secondsToHMS(sectionStartSec)
      if (sectionEndSec < videoDuration) config.end_time = secondsToHMS(sectionEndSec)

      // Initialize Ably client for real-time updates
      if (!ablyClientRef.current) {
        ablyClientRef.current = createAblyClient(getToken)
      }

      // Start clip detection — returns immediately with content_id
      const result = await api.tools.startClipDetection(
        transcript.youtube_video_id,
        config,
        token || undefined,
      )

      // Handle the JSON response
      const data = result.data || result
      if (data.content_id) {
        setContentId(data.content_id)
        setVideoId(data.video_id)
        subscribeToContent(data.content_id)
      } else {
        setProcessingError('Failed to start clip generation. Please try again.')
      }
    } catch (err: any) {
      if (!processingError) {
        let msg = err.message || 'Failed to start clip generation. Please try again.'
        try {
          const parsed = JSON.parse(msg)
          if (Array.isArray(parsed?.detail)) {
            msg = parsed.detail.map((d: any) => d.msg || d.message || '').filter(Boolean).join('. ') || msg
          } else if (typeof parsed?.detail === 'string') {
            msg = parsed.detail
          }
        } catch {
          // Not JSON, use as-is
        }
        setProcessingError(msg)
      }
    }
  }

  // ============================================================================
  // Phase 4: Results handlers
  // ============================================================================

  const handleClipClick = (clipId: string) => {
    dfTrackClipExportClicked({ source: 'tool_page', tool_slug: tool.slug, clip_id: clipId })
    if (contentId) {
      router.push(`/clips/${contentId}/edit/${clipId}`)
    }
  }

  const sortedClips = [...detectedClips].sort((a, b) => b.virality_score - a.virality_score)
  const sortedClipData = sortedClips.map(c => toClipData(c, contentId || '', transcript?.thumbnail_url))

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      {/* Paywall Modal */}
      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        videoTitle={transcript?.title}
        creditsInfo={creditsInfo}
        surface="viral_clips"
        toolSlug={tool.slug}
      />

      {/* Auth Modal Overlay */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAuthModal(false)} />
          <div className="relative z-10">
            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute -top-3 -right-3 z-20 w-8 h-8 rounded-full bg-white dark:bg-zinc-800 shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            >
              <X className="h-4 w-4" />
            </button>
            {(() => {
              const redirectUrl = `${window.location.origin}/tools/${tool.slug}${url ? `?url=${encodeURIComponent(url)}` : ''}`
              return authMode === 'sign-up' ? (
                <SignUp routing="hash" forceRedirectUrl={redirectUrl} signInForceRedirectUrl={redirectUrl} />
              ) : (
                <SignIn routing="hash" forceRedirectUrl={redirectUrl} signUpForceRedirectUrl={redirectUrl} />
              )
            })()}
            <div className="text-center mt-3">
              {authMode === 'sign-up' ? (
                <button onClick={() => setAuthMode('sign-in')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Already have an account? <span className="text-[#ff6b4a] font-medium">Sign in</span>
                </button>
              ) : (
                <button onClick={() => setAuthMode('sign-up')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Don&apos;t have an account? <span className="text-[#ff6b4a] font-medium">Sign up free</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <Header />

      {/* Hero Section with URL Input */}
      <section className="border-b border-border pt-16">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#CC3E21]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ToolIcon className="h-10 w-10 text-[#ff6b4a]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{tool.h1}</h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">{tool.long_description}</p>

            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mb-4">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Paste YouTube URL here..."
                className="flex-1 h-12 bg-muted/50 border border-border focus:border-[#CC3E21]/50 text-base"
                disabled={loading || phase !== 'input'}
              />
              <Button
                onClick={handleSubmit}
                disabled={!url.trim() || loading || phase !== 'input'}
                size="lg"
                className="h-12 bg-[#CC3E21] hover:bg-[#b5351b] text-white px-8 font-semibold whitespace-nowrap"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  'Analyze Video'
                )}
              </Button>
            </div>

            {error && (
              <div className="flex items-center justify-center gap-2 text-red-600 text-sm mb-4">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            {phase === 'input' && (
              <p className="text-sm text-muted-foreground mt-4">No credit card required &bull; Free to start &bull; Instant results</p>
            )}
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* Phase 2: Configuration UI */}
      {/* ================================================================== */}
      {phase === 'configure' && transcript && (
        <section ref={workspaceRef} className="py-8 border-b bg-muted/10">
          <div className="container mx-auto px-4 max-w-2xl">
            {/* Video info row */}
            <div className="flex gap-3 mb-6">
              <div className="relative w-32 flex-shrink-0 rounded-lg overflow-hidden aspect-video">
                {transcript.thumbnail_url && (
                  <img src={transcript.thumbnail_url} alt={transcript.title || ''} className="w-full h-full object-cover" />
                )}
                {transcript.duration && (
                  <div className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1 py-0.5 rounded font-medium">
                    {formatDuration(transcript.duration)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-foreground text-[13px] line-clamp-2 leading-snug">{transcript.title}</h2>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                  <span>{transcript.channel_name}</span>
                  <span className="opacity-40">&middot;</span>
                  <span>{transcript.language || 'en'}</span>
                  {transcript.transcript?.snippets && (
                    <>
                      <span className="opacity-40">&middot;</span>
                      <span>{transcript.transcript.snippets.length} segments</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Duration limit notice */}
            {rawDuration > sectionCap && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-600 dark:text-amber-400">
                This video is {formatDuration(rawDuration)} long.{' '}
                {creditsBalance === null
                  ? `A free account includes ${FREE_PLAN_CREDITS} credits`
                  : `You have ${creditsBalance} credits`}
                , and 1 credit covers 1 minute, so you can clip up to {formatDuration(sectionCap)} at a time.
                Use the range slider below to select which section to analyze.
              </div>
            )}

            {/* Range Slider */}
            {videoDuration > 0 && (
              <div className="mb-5 px-1">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-medium text-muted-foreground">Range</span>
                  <span className={`text-[11px] font-medium ${isRangeNarrowed ? 'text-[#CC3E21]' : 'text-muted-foreground'}`}>
                    {formatSliderTime(sectionStartSec)} — {formatSliderTime(sectionEndSec)}
                    {isRangeNarrowed && ` (${formatSliderTime(sectionEndSec - sectionStartSec)})`}
                  </span>
                </div>
                <Slider
                  value={[sectionStartSec, sectionEndSec]}
                  min={0}
                  max={videoDuration}
                  step={1}
                  onValueChange={handleSliderChange}
                  className="[&_[data-slot=slider-range]]:bg-[#CC3E21] [&_[data-slot=slider-thumb]]:border-[#CC3E21] [&_[data-slot=slider-track]]:h-1.5 [&_[data-slot=slider-thumb]]:size-4"
                />
              </div>
            )}

            {/* Settings rows */}
            <div className="mb-5 space-y-3">
              {/* Row 1: Clip Length + Hook */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">Clip length</span>
                  <div className="flex gap-1">
                    {clipLengths.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setClipLength(opt.value)}
                        className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                          clipLength === opt.value
                            ? 'bg-[#CC3E21] text-white'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] font-medium text-muted-foreground">Hook</span>
                  <Switch checked={autoHook} onCheckedChange={setAutoHook} className="scale-90" />
                </div>
              </div>

              {/* Row 2: Format */}
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">Format</span>
                <div className="flex gap-1">
                  {FORMATS.map(fmt => (
                    <button
                      key={fmt.value}
                      onClick={() => setSelectedFormat(fmt.value)}
                      className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                        selectedFormat === fmt.value
                          ? 'bg-[#CC3E21] text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {fmt.label}
                      <span className="ml-1 opacity-60">{fmt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-border mb-5" />

            {/* Caption Style Carousel */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[13px] font-semibold text-foreground">Caption Style</h3>
                <div className="flex items-center gap-1">
                  <button onClick={() => scrollCarousel('left')} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button onClick={() => scrollCarousel('right')} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div ref={carouselRef} className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-none" style={{ scrollbarWidth: 'none' }}>
                {CAPTION_TEMPLATES.map((template, index) => (
                  <button
                    key={template.name}
                    onClick={() => setSelectedCaptionIndex(index)}
                    className={`flex-shrink-0 w-[72px] rounded-lg border-2 transition-all overflow-hidden ${
                      selectedCaptionIndex === index
                        ? 'border-[#CC3E21] ring-1 ring-[#CC3E21]/30 scale-[1.02]'
                        : 'border-transparent hover:border-foreground/10'
                    }`}
                  >
                    <div className="aspect-[9/16] bg-zinc-900 flex items-end justify-center p-1.5 relative">
                      <p
                        className="text-center leading-tight"
                        style={{
                          fontSize: '9px',
                          fontFamily: template.style.fontFamily,
                          fontWeight: template.style.fontWeight as any,
                          textTransform: template.style.textTransform as any,
                          WebkitTextStroke: template.style.strokeWidth > 0
                            ? `${Math.max(0.5, template.style.strokeWidth * 0.05)}px ${template.style.strokeColor}`
                            : undefined,
                          textShadow: template.style.shadowEnabled
                            ? `${template.style.shadowOffsetX ?? 1}px ${template.style.shadowOffsetY ?? 1}px ${template.style.shadowBlur ?? 2}px ${template.style.shadowColor ?? '#000'}`
                            : undefined,
                        }}
                      >
                        <span style={{ color: template.style.activeWordEnabled ? template.style.activeWordColor : template.style.color }}>
                          VIRAL
                        </span>{' '}
                        <span style={{ color: template.style.color }}>CLIPS</span>
                      </p>
                    </div>
                    <div className="py-1 bg-card">
                      <p className="text-[9px] font-medium text-foreground text-center truncate px-1">{template.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Get Clips CTA */}
            <Button
              onClick={handleGetClips}
              className="w-full h-12 bg-[#CC3E21] hover:bg-[#b5351b] text-white rounded-lg font-semibold text-sm tracking-wide uppercase"
            >
              <Scissors className="h-4 w-4 mr-2" />
              Get clips in 1 click
            </Button>

            {!isSignedIn && (
              <p className="text-center text-[11px] text-muted-foreground mt-3">
                Free account required &bull; Sign up takes 10 seconds
              </p>
            )}
          </div>
        </section>
      )}

      {/* ================================================================== */}
      {/* Phase 3: Processing with progress */}
      {/* ================================================================== */}
      {phase === 'processing' && (
        <section ref={workspaceRef} className="py-8 border-b bg-muted/10">
          <div className="container mx-auto px-4 max-w-3xl">
            {/* Progress card with video preview */}
            <Card className="overflow-hidden border-[#CC3E21]/20 mb-6">
              {processingError ? (
                <div className="flex flex-col items-center justify-center py-8 px-6">
                  <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
                  <p className="text-sm text-red-400 mb-4 text-center">{processingError}</p>
                  <Button onClick={() => { setPhase('configure'); setProcessingError(null) }} size="sm" variant="outline">
                    Back to Settings
                  </Button>
                </div>
              ) : (
                <>
                  {/* Video preview banner */}
                  {transcript && (
                    <div className="relative bg-muted/50">
                      <div className="flex items-center gap-4 p-4">
                        {/* Thumbnail */}
                        <div className="relative flex-shrink-0 w-28 h-16 rounded-md overflow-hidden bg-muted">
                          {transcript.thumbnail_url ? (
                            <img
                              src={transcript.thumbnail_url}
                              alt={transcript.title || 'Video thumbnail'}
                              className="w-full h-full object-cover opacity-80"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Video className="w-8 h-8 text-white/40" />
                            </div>
                          )}
                          {/* Scanning overlay animation */}
                          <div className="absolute inset-0 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#CC3E21]/30 to-transparent animate-[shimmer_2s_infinite]" style={{ animationTimingFunction: 'ease-in-out' }} />
                          </div>
                          <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 rounded text-[10px] font-mono text-white/80">
                            {transcript.duration ? formatDuration(transcript.duration) : ''}
                          </div>
                        </div>
                        {/* Video info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{transcript.title || 'Untitled Video'}</p>
                          <p className="text-xs text-muted-foreground truncate">{transcript.channel_name || ''}</p>
                        </div>
                        <div className="flex-shrink-0">
                          <Scissors className="w-5 h-5 text-[#CC3E21] animate-pulse" />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Progress section */}
                  <div className="p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Sparkles className="w-5 h-5 text-[#CC3E21]" />
                        <Loader2 className="absolute -top-1 -right-1 w-3 h-3 text-[#CC3E21] animate-spin" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{currentStep || 'Starting...'}</p>
                        <p className="text-[11px] text-muted-foreground">AI is scanning your video for viral-worthy moments</p>
                      </div>
                    </div>
                    <Progress value={progressPercent} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{progressPercent}% complete</span>
                      {detectedClips.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Sparkles className="h-3 w-3 text-[#ff6b4a]" />
                          {detectedClips.length} clip{detectedClips.length !== 1 ? 's' : ''} found
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </Card>

            {/* Clips appearing incrementally — carousel */}
            {detectedClips.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Clips detected so far</h3>
                <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
                  {detectedClips
                    .sort((a, b) => b.virality_score - a.virality_score)
                    .map(clip => (
                      <div key={clip.id} className="flex-shrink-0 w-[160px] snap-start">
                        <ClipCard
                          clip={toClipData(clip, contentId ?? undefined, transcript?.thumbnail_url)}
                          onClick={() => handleClipClick(clip.id)}
                        />
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================================================================== */}
      {/* Phase 4: Results */}
      {/* ================================================================== */}
      {phase === 'results' && (
        <section ref={workspaceRef} className="py-8 border-b bg-muted/10">
          <div className="container mx-auto px-8 max-w-6xl">
            {/* Success banner */}
            <Card className="p-4 border-emerald-500/20 bg-emerald-500/5 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center">
                  <Check className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Found {sortedClips.length} viral clip{sortedClips.length !== 1 ? 's' : ''}!</p>
                  <p className="text-xs text-muted-foreground">Click any clip to open it in your project editor</p>
                </div>
                {videoId && (
                  <Button
                    onClick={() => router.push(`/v/${videoId}`)}
                    size="sm"
                    className="ml-auto bg-[#CC3E21] hover:bg-[#b5351b] text-white gap-1"
                  >
                    Open Project <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>

            {/* Clip carousel */}
            <div className="relative">
              {/* Scroll arrows */}
              {sortedClipData.length > 4 && (
                <>
                  <button
                    onClick={() => resultsScrollRef.current?.scrollBy({ left: -400, behavior: 'smooth' })}
                    className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors hidden md:flex"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => resultsScrollRef.current?.scrollBy({ left: 400, behavior: 'smooth' })}
                    className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors hidden md:flex"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
              <div ref={resultsScrollRef} className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
                {sortedClipData.map((clip) => (
                  <div key={clip.id} className="flex-shrink-0 w-[180px] snap-start">
                    <ClipCard
                      clip={clip}
                      onClick={() => handleClipClick(clip.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================== */}
      {/* SEO Sections (shared across all phases) */}
      {/* ================================================================== */}

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">How to Use {tool.name}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-[#ff6b4a]">1</span>
                </div>
                <h3 className="font-semibold mb-2">Paste YouTube URL</h3>
                <p className="text-sm text-muted-foreground">Copy any YouTube video link and paste it into our tool.</p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-[#ff6b4a]">2</span>
                </div>
                <h3 className="font-semibold mb-2">Configure & Generate</h3>
                <p className="text-sm text-muted-foreground">Choose your format, clip length, and caption style, then click generate.</p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-[#ff6b4a]">3</span>
                </div>
                <h3 className="font-semibold mb-2">Get Your Clips</h3>
                <p className="text-sm text-muted-foreground">AI finds the most viral moments and creates clips ready for editing and export.</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Perfect For</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {tool.use_cases.map((useCase: string, index: number) => (
                <div key={index} className="flex gap-3 items-start">
                  <Check className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-muted-foreground">{useCase}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Example Use Cases</h2>
            <div className="space-y-4">
              {tool.examples.map((example: string, index: number) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ToolIcon className="h-4 w-4 text-[#ff6b4a]" />
                    </div>
                    <p>{example}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools */}
      {relatedTools.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Related Tools</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedTools.map((relatedTool: any) => {
                  const RelatedIcon = getIconByName(relatedTool.icon_name)
                  return (
                    <Link key={relatedTool.id} href={`/tools/${relatedTool.slug}`}>
                      <Card className="p-6 hover:border-[#CC3E21]/50 transition-colors cursor-pointer h-full">
                        <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                          <RelatedIcon className="h-6 w-6 text-[#ff6b4a]" />
                        </div>
                        <h3 className="font-semibold mb-2">{relatedTool.name}</h3>
                        <p className="text-sm text-muted-foreground">{relatedTool.short_description}</p>
                      </Card>
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Category Tools */}
      {categoryTools.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">
                More {tool.category.charAt(0).toUpperCase() + tool.category.slice(1)} Tools
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {categoryTools.map((categoryTool: any) => {
                  const CategoryIcon = getIconByName(categoryTool.icon_name)
                  return (
                    <Link key={categoryTool.id} href={`/tools/${categoryTool.slug}`}>
                      <Card className="p-6 hover:border-[#CC3E21]/50 transition-colors cursor-pointer h-full">
                        <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                          <CategoryIcon className="h-6 w-6 text-[#ff6b4a]" />
                        </div>
                        <h3 className="font-semibold mb-2">{categoryTool.name}</h3>
                        <p className="text-sm text-muted-foreground">{categoryTool.short_description}</p>
                      </Card>
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground mb-8">Start using {tool.name} for free. No credit card required.</p>
            <Button
              size="lg"
              className="gap-2"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
                setTimeout(() => document.querySelector<HTMLInputElement>('input[placeholder*="YouTube"]')?.focus(), 600)
              }}
            >
              Try {tool.name} Free <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ — tool-specific from config, with generic fallbacks */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {(tool.faqs && tool.faqs.length > 0) ? (
                tool.faqs.map((faq: { q: string; a: string }, index: number) => (
                  <Card key={index} className="p-6">
                    <h3 className="font-semibold mb-2">{faq.q}</h3>
                    <p className="text-muted-foreground">{faq.a}</p>
                  </Card>
                ))
              ) : (
                <>
                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">Is {tool.name} really free?</h3>
                    <p className="text-muted-foreground">
                      Yes! Clip detection and preview are completely free. Export and advanced editing features require a paid plan.
                    </p>
                  </Card>
                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">What video lengths can I process?</h3>
                    <p className="text-muted-foreground">
                      Free accounts can clip videos up to 30 minutes long. Paid plans go up to 60 minutes, from short clips to full-length podcasts and lectures.
                    </p>
                  </Card>
                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">How many clips will I get?</h3>
                    <p className="text-muted-foreground">
                      Depending on the video length and content, our AI typically finds 5-15 viral moments per video, each scored by viral potential.
                    </p>
                  </Card>
                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">Can I use this for commercial purposes?</h3>
                    <p className="text-muted-foreground">
                      Yes! Premium subscribers can use generated clips for commercial purposes including social media, marketing, and content distribution.
                    </p>
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  )
}
